import {AxiosResponse} from "axios";
import DeprecatedGrouping from "@/core/interfaces/laboratory/Grouping";
import {
    Healthcenter,
    HealthcenterLaboratorySharing, HealthcenterLog,
    LegacyHealthcenterOpening
} from "@/core/interfaces/healthcenter/Healthcenter";
import {OpeningHoursRequest} from "@/core/interfaces/healthcenter/OpeningHoursRequest";

/**
 * HealthcenterGroupingApiResponse interface.
 *
 * @property {DeprecatedGrouping} grouping - The healthcenter's grouping.
 */
interface HealthcenterGroupingApiResponse {
    grouping: DeprecatedGrouping;
}

/**
 * The healthcenters product resource.
 *
 * @param {Object} client - The injected client.
 * @returns {Object} The public object.
 */
export default function (client) {
    return {
        /**
         * Get healthCenter information.
         *
         * @param {number} id - The healthcenter id.
         *
         * @return {Promise<LegacyHealthcenter>}
         */
        read(id: number): Promise<Healthcenter> {
            return client.get(`/healthcenters/${id}`)
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Update healthCenter information.
         *
         * @param {number} healthCenterId - HealthCenter id.
         * @param {object} data - New healthCenter information.
         *
         * @return {Promise<any>}
         */
        update(healthCenterId: number, data: any): Promise<any> {
            return client.put(`/healthcenters/${healthCenterId}`, data)
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Get the healthcenter grouping.
         *
         * @param {number} healthcenterId - The healthcenter id.
         *
         * @returns {Promise<DeprecatedGrouping>}
         */
        healthcenterGrouping(healthcenterId: number): Promise<DeprecatedGrouping> {
            const params = {
                include: "branding.logo"
            };

            return client.get(`healthcenters/${healthcenterId}/branding`, {params})
                .then((response: AxiosResponse<HealthcenterGroupingApiResponse | null>) => response.data?.grouping);
        },

        /**
         * Create healthCenter opening hours.
         *
         * @param {number} id - Healthcenter id.
         * @param {LegacyHealthcenterOpening} data - The opening hours.
         *
         * @returns {Promise<LegacyHealthcenterOpening>}
         */
        createOpening(id: number, data: OpeningHoursRequest): Promise<LegacyHealthcenterOpening> {
            return client.post(`/healthcenters/${id}/opening`, data)
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Update healthCenter opening hours.
         *
         * @param {number} openingHourId - The opening hour id.
         * @param {LegacyHealthcenterOpening} data - The new opening hours.
         *
         * @returns {Promise<LegacyHealthcenterOpening>}
         */
        updateOpening(openingHourId: number, data: OpeningHoursRequest): Promise<LegacyHealthcenterOpening> {
            return client.put(`/healthcenters/opening/${openingHourId}`, data)
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Upload a healthCenter picture.
         *
         * @param {number} healthCenterId - The healthCenter id.
         * @param {number} fileId - The file id to post.
         *
         * @return {Promise<any>}
         */
        uploadMedia(healthCenterId: number, fileId: number): Promise<any> {
            const params = {
                filemanager_file_id: fileId
            };

            return client.post(`/healthcenters/${healthCenterId}/medias`, params)
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Delete a healthCenter picture.
         *
         * @param {number} mediaId - The healthCenter media id.
         *
         * @return {Promise<any>}
         */
        deleteMedia(mediaId: number): Promise<any> {
            return client.delete(`/healthcenters/medias/${mediaId}`)
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Get laboratory sharing.
         *
         * @param healthcenterId
         * @param laboratoryId
         */
        async laboratorySharing(healthcenterId: number, laboratoryId: number): Promise<HealthcenterLaboratorySharing> {
            return client.get(`/healthcenters/${healthcenterId}/laboratories/${laboratoryId}/sharing`)
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Update laboratory sharing.
         *
         * @param healthcenterId
         * @param laboratoryId
         * @param accepted
         */
        async updateLaboratorySharing(healthcenterId: number, laboratoryId: number, accepted: boolean): Promise<HealthcenterLaboratorySharing> {
            return client.patch(`/healthcenters/${healthcenterId}/laboratories/${laboratoryId}/sharing`, {is_accepted: accepted})
                .then((response: AxiosResponse) => response.data.data);
        },

        async getLog(healthcenterId: number): Promise<HealthcenterLog> {
            return client.get(`/healthcenters/${healthcenterId}/log`)
                .then((response: AxiosResponse) => response.data.data);
        }
    };
};
